<script setup lang="ts">
  import '@/assets/calendar-legal-os.css'
  import { buildMiniMonthGrid, startOfDay } from '@/utils/calendar/date'
  import { getMonthNames } from '@/utils/calendar/month-names'
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'

  const props = defineProps<{
    selectedDate: Date
    today: Date
    weekdayLabels: string[]
    eventDates: Set<string>
  }>()

  const emit = defineEmits<{
    'select-date': [date: Date]
  }>()

  const { locale } = useI18n()

  const yearMonths = computed(() => {
    const year = props.selectedDate.getFullYear()
    const { full: monthNames } = getMonthNames(locale.value)
    return monthNames.map((name, index) => {
      const anchor = new Date(year, index, 1)
      const cells = buildMiniMonthGrid({
        anchor,
        selected: props.selectedDate,
        today: props.today,
        highlightWeek: false,
        datesWithEvents: props.eventDates
      })
      const weeks: (typeof cells)[] = []
      for (let i = 0; i < cells.length; i += 7) {
        weeks.push(cells.slice(i, i + 7))
      }
      return { index, name, weeks }
    })
  })

  const jumpToDay = (date: Date): void => {
    emit('select-date', startOfDay(date))
  }
</script>

<template>
  <div
    class="calendar-main bg-layer flex h-full min-h-0 flex-1 flex-col overflow-hidden"
    data-cy="calendar-year-view"
  >
    <div class="flex min-h-0 flex-1 items-center justify-center overflow-hidden px-4 py-4">
      <div class="grid w-full max-w-[68rem] grid-cols-4 grid-rows-3 gap-x-10 gap-y-6">
        <div
          v-for="m in yearMonths"
          :key="m.index"
          class="mx-auto w-56 min-w-0 xl:w-64"
        >
          <span class="text-muted-foreground-2 mb-2 block ps-1.5 text-xs font-medium">
            {{ m.name }}
          </span>

          <div class="mb-1.5 flex justify-between">
            <span
              v-for="label in weekdayLabels"
              :key="`${m.index}-${label}`"
              class="text-muted-foreground-1 m-px block w-7 text-center text-[11px]"
            >
              {{ label }}
            </span>
          </div>

          <div
            v-for="(week, wi) in m.weeks"
            :key="wi"
            class="flex justify-between"
          >
            <button
              v-for="(cell, ci) in week"
              :key="ci"
              type="button"
              :disabled="!cell.currentMonth"
              :class="[
                `relative m-px inline-flex size-7 items-center justify-center rounded-full text-xs focus:outline-none
                disabled:pointer-events-none disabled:opacity-50`,
                cell.isSelected
                  ? 'border-primary-line border bg-primary font-medium text-primary-foreground hover:bg-primary-hover'
                  : cell.isToday
                    ? `hover:bg-surface-hover focus-visible:bg-surface-focus border border-primary/40 font-medium
                      text-primary`
                    : cell.currentMonth
                      ? 'hover:bg-surface-hover focus-visible:bg-surface-focus border border-transparent text-foreground'
                      : 'hover:bg-surface-hover focus-visible:bg-surface-focus text-muted-foreground/70'
              ]"
              @click="cell.currentMonth && jumpToDay(cell.date)"
            >
              {{ cell.day }}
              <span
                v-if="cell.hasEvent && !cell.isSelected"
                class="absolute bottom-0.5 left-1/2 size-1 -translate-x-1/2 rounded-full bg-primary"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
